import { readRequestId, REQUEST_ID_PATTERN } from './requestReference';
import { desktopFailureReference } from '@/desktop/desktopFailureReference';

/** One entry of a GraphQL response's `errors` array, as far as the web app reads it. */
export interface IGraphqlErrorPayload {
  message: string;
  extensions?: Record<string, unknown>;
  /** Where in the answer the error is: `['workItemsPage', 'items', 3, 'updatedAt']`. */
  path?: ReadonlyArray<string | number>;
}

const OPERATION_NAME = /^\s*(?:query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/m;

/** The operation a GraphQL document names: `WorkItemsPage` in `query WorkItemsPage(...)`, or null. */
export function graphqlOperationName(document: string): string | null {
  return OPERATION_NAME.exec(document)?.[1] ?? null;
}

/**
 * A GraphQL rejection that keeps its `extensions`, and where it came from.
 *
 * Both transports used to throw `new Error(errors[0].message)`, which kept the prose and dropped the
 * structure. A refusal a client is meant to act on — "E: joker holds this", with the token to send
 * back (I-0383) — then reaches the screen only as a sentence to parse.
 *
 * I-0935 (`docs/concept-failure-messages.md`, EM-1): it also keeps the operation it answered and the
 * error's field path. I-0114's intermittent failure on the Work items list could not be placed,
 * because the page runs several operations and the error named none. `message` stays the server's
 * sentence; `logReference` is for a log, never for the screen (I-1470, EM-8): a person reads the
 * server's request id instead, which the server's own log line carries beside the path.
 */
export class GraphqlRequestError extends Error {
  readonly extensions: Record<string, unknown>;
  /** The operation this error answered, from the query's own name. */
  readonly operation: string | null;
  /** The error's path in the answer, joined with `.`, when the server gave one. */
  readonly path: string | null;

  constructor(error: IGraphqlErrorPayload, operation: string | null = null) {
    super(error.message);
    this.name = 'GraphqlRequestError';
    this.extensions = error.extensions ?? {};
    this.operation = operation;
    this.path = error.path?.length ? error.path.join('.') : null;
    // A log names where it came from: the stack's first line is what a console prints first.
    const reference = this.logReference;
    if (reference && typeof this.stack === 'string') {
      this.stack = this.stack.replace(
        /^[^\n]*/,
        () => `${this.name}: ${this.message} [${reference}]`,
      );
    }
  }

  /**
   * `WorkItemsPage › workItemsPage.items.3.updatedAt`, or null when neither is known. For a log
   * only: `failureReference.guard.test` fails a reader of it outside this file.
   */
  get logReference(): string | null {
    if (!this.operation && !this.path) return null;
    return [this.operation ?? 'unnamed operation', this.path].filter(Boolean).join(' › ');
  }

  /**
   * The id the server gave a failure it did not mean (`redactUnexpectedGraphqlError`), under which
   * its log holds the path and the cause. A refusal it meant carries none.
   */
  get requestId(): string | null {
    const id = this.extensions.requestId;
    return typeof id === 'string' && REQUEST_ID_PATTERN.test(id) ? id : null;
  }
}

/**
 * Whether the server marked this rejection one no retry will get past (I-1015): the backend's
 * `markPermanentClientErrors` stamps `extensions.permanent: true` on a GraphQL error whose cause is
 * the payload (400, 413, 415, 422) — `"Token usage needs a binStart lower bound."` is one. A client
 * that cannot tell this from a network blip asks the identical doomed question forever, which is
 * exactly the failure `graphql-error-permanence.ts` (backend) was written to end.
 *
 * ⛔ **Absence is not a denial.** `false`, missing, or an error that is not a `GraphqlRequestError` at
 * all answers `false` here — "decide for yourself" (the backend's own doc comment), never "this one
 * is safe to retry".
 */
export function isPermanentServerError(error: unknown): boolean {
  return error instanceof GraphqlRequestError && error.extensions.permanent === true;
}

/** The status and code a read carries when it ran out of time, on the server or on the way. */
const REQUEST_TIMEOUT_STATUS = 408;
const REQUEST_TIMEOUT_CODE = 'REQUEST_TIMEOUT';
const TRANSPORT_TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT']);

/**
 * Whether a read failed because it ran out of time (I-1305): the server stopped its query at the
 * deployment's limit and said so (`REQUEST_TIMEOUT`, 408), or the transport gave up waiting.
 *
 * Asking such a read again at once costs the server the same time again, so the page asks it less
 * and less often (`DATASET_SYNC_SLOW_READ_RETRY_DELAYS_MS`) rather than at the cadence of a blip.
 */
export function isReadTimeoutError(error: unknown): boolean {
  if (error instanceof GraphqlRequestError) {
    const original = error.extensions.originalError as { statusCode?: unknown } | undefined;
    return (
      error.extensions.code === REQUEST_TIMEOUT_CODE ||
      error.extensions.status === REQUEST_TIMEOUT_STATUS ||
      original?.statusCode === REQUEST_TIMEOUT_STATUS
    );
  }
  const transport = error as { code?: unknown; response?: { status?: unknown } } | null;
  return (
    transport?.response?.status === REQUEST_TIMEOUT_STATUS ||
    (typeof transport?.code === 'string' && TRANSPORT_TIMEOUT_CODES.has(transport.code))
  );
}

/**
 * The limit the server stopped a read at, in milliseconds, when it said (I-2111): `limitMs` in the 408's
 * body, on a GraphQL error's `originalError` or a REST response's data. Null from a server that sent none,
 * and for a read the transport gave up on.
 */
export function readTimeoutLimitMs(error: unknown): number | null {
  const body =
    error instanceof GraphqlRequestError
      ? (error.extensions.originalError as { limitMs?: unknown } | undefined)
      : (error as { response?: { data?: { limitMs?: unknown } } } | null)?.response?.data;
  const limit = body?.limitMs;
  return typeof limit === 'number' && Number.isFinite(limit) && limit > 0 ? limit : null;
}

/**
 * The reference a screen shows for a failure: the request id the server gave it, on either kind of
 * call, as minted (I-1470, `docs/concept-failure-messages.md` EM-8, EM-9, EM-12, EM-13).
 * Null when it gave none.
 *
 * ⛔ **Never an operation, a field path or a route.** "Reference: SaveChallenge › saveChallenge." is
 * code words where a person reads what happened and what to do; `failureReference.guard.test` puts
 * every operation the web declares through every describer to keep it so.
 */
export function failureReference(error: unknown): string | null {
  // Shown as minted (EM-13): the text on the screen is the text a search of the server's log finds.
  if (error instanceof GraphqlRequestError) return error.requestId;
  // F-0689 DG-9: a desktop failure's reference is the one the app's own log line holds.
  return readRequestId(error) ?? desktopFailureReference(error);
}
